import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Button,
  Table,
  Statistic,
  DatePicker,
  Drawer,
  Descriptions,
  Divider,
  Space,
  Spin,
  message,
} from 'antd';
import {
  DollarOutlined,
  FileTextOutlined,
  DownloadOutlined,
  EyeOutlined,
  HistoryOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { subscriptionApi } from '../../services/subscription';
import type { Invoice } from '../../types/subscription';
import { COLORS } from '../../constants/colors';

const { Title, Text } = Typography;

// 状态颜色映射
const statusColorMap: Record<Invoice['status'], string> = {
  pending: 'orange',
  paid: 'green',
  failed: 'red',
  refunded: 'blue',
};

// 状态文案映射
const statusTextMap: Record<Invoice['status'], string> = {
  pending: '待支付',
  paid: '已支付',
  failed: '支付失败',
  refunded: '已退款',
};

export const BillingManagement: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [bills, setBills] = useState<Invoice[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [billingMonth, setBillingMonth] = useState<string | undefined>(undefined);
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentBill, setCurrentBill] = useState<Invoice | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchBills = useCallback(async () => {
    setLoading(true);
    try {
      const res = await subscriptionApi.getBills({
        page,
        page_size: pageSize,
        billing_month: billingMonth,
      });
      if (res.data) {
        setBills(res.data.list || []);
        setTotal(res.data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch bills:', error);
      message.error('获取账单列表失败');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, billingMonth]);

  useEffect(() => {
    fetchBills();
  }, [fetchBills]);

  // 统计数据
  const currentMonthBills = bills.filter(
    (b) => b.billing_month === dayjs().format('YYYY-MM')
  );
  const currentMonthTotal = currentMonthBills.reduce(
    (sum, b) => sum + b.total_amount,
    0
  );
  const historyTotal = bills.reduce((sum, b) => sum + b.total_amount, 0);
  const pendingCount = bills.filter((b) => b.status === 'pending').length;

  // 查看详情
  const handleViewDetail = async (record: Invoice) => {
    setDetailVisible(true);
    setDetailLoading(true);
    try {
      const res = await subscriptionApi.getBillDetail(record.id);
      if (res.data) {
        setCurrentBill(res.data);
      } else {
        setCurrentBill(record);
      }
    } catch {
      setCurrentBill(record);
    } finally {
      setDetailLoading(false);
    }
  };

  // 下载发票
  const handleDownload = (url: string) => {
    window.open(url, '_blank');
  };

  // 表格列定义
  const columns = [
    {
      title: '账单号',
      dataIndex: 'invoice_no',
      key: 'invoice_no',
      width: 180,
      render: (text: string) => <Text copyable={{ text }}>{text}</Text>,
    },
    {
      title: '账单月份',
      dataIndex: 'billing_month',
      key: 'billing_month',
      width: 110,
    },
    {
      title: '订阅费用',
      dataIndex: 'subscription_fee',
      key: 'subscription_fee',
      width: 110,
      render: (val: number) => <Text>¥{val.toFixed(2)}</Text>,
    },
    {
      title: '超额费用',
      dataIndex: 'overage_fee',
      key: 'overage_fee',
      width: 110,
      render: (val: number) => <Text>¥{val.toFixed(2)}</Text>,
    },
    {
      title: '增值服务费',
      dataIndex: 'addon_fee',
      key: 'addon_fee',
      width: 110,
      render: (val: number) => <Text>¥{val.toFixed(2)}</Text>,
    },
    {
      title: '折扣',
      dataIndex: 'discount_amount',
      key: 'discount_amount',
      width: 100,
      render: (val: number) =>
        val > 0 ? <Text type="success">-¥{val.toFixed(2)}</Text> : <Text>-</Text>,
    },
    {
      title: '总金额',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 120,
      render: (val: number) => <Text strong>¥{val.toFixed(2)}</Text>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: Invoice['status']) => (
        <Tag color={statusColorMap[status]}>{statusTextMap[status]}</Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      render: (_: unknown, record: Invoice) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            详情
          </Button>
          {record.invoice_url && (
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record.invoice_url!)}
            >
              下载
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <div>
        <Title level={4} style={{ marginBottom: 24 }}>
          账单管理
        </Title>

        {/* 统计卡片 */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={8}>
            <Card>
              <Statistic
                title="本月应付"
                value={currentMonthTotal}
                precision={2}
                prefix={<DollarOutlined style={{ color: COLORS.primary }} />}
                suffix="元"
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="历史总计"
                value={historyTotal}
                precision={2}
                prefix={<HistoryOutlined style={{ color: COLORS.success }} />}
                suffix="元"
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="待支付账单"
                value={pendingCount}
                prefix={<ExclamationCircleOutlined style={{ color: pendingCount > 0 ? COLORS.warning : undefined }} />}
                suffix="笔"
                valueStyle={{ color: pendingCount > 0 ? COLORS.warning : undefined }}
              />
            </Card>
          </Col>
        </Row>

        {/* 账单列表 */}
        <Card
          title={
            <Space>
              <FileTextOutlined />
              <span>账单列表</span>
            </Space>
          }
          extra={
            <DatePicker
              picker="month"
              placeholder="按月份筛选"
              allowClear
              onChange={(date) => {
                setBillingMonth(date ? date.format('YYYY-MM') : undefined);
                setPage(1);
              }}
            />
          }
        >
          <Table
            columns={columns}
            dataSource={bills}
            rowKey="id"
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              showTotal: (t) => `共 ${t} 条记录`,
              onChange: (p, ps) => {
                setPage(p);
                setPageSize(ps);
              },
            }}
            scroll={{ x: 1100 }}
          />
        </Card>

        {/* 账单详情抽屉 */}
        <Drawer
          title="账单详情"
          open={detailVisible}
          onClose={() => {
            setDetailVisible(false);
            setCurrentBill(null);
          }}
          width={520}
        >
          <Spin spinning={detailLoading}>
            {currentBill && (
              <>
                {/* 基本信息 */}
                <Descriptions title="基本信息" column={1} bordered size="small">
                  <Descriptions.Item label="账单号">
                    {currentBill.invoice_no}
                  </Descriptions.Item>
                  <Descriptions.Item label="账单月份">
                    {currentBill.billing_month}
                  </Descriptions.Item>
                  <Descriptions.Item label="创建时间">
                    {dayjs(currentBill.created_at).format('YYYY-MM-DD HH:mm:ss')}
                  </Descriptions.Item>
                </Descriptions>

                <Divider />

                {/* 费用明细 */}
                <Descriptions title="费用明细" column={1} bordered size="small">
                  <Descriptions.Item label="订阅费用">
                    ¥{currentBill.subscription_fee.toFixed(2)}
                  </Descriptions.Item>
                  <Descriptions.Item label="超额费用">
                    ¥{currentBill.overage_fee.toFixed(2)}
                  </Descriptions.Item>
                  <Descriptions.Item label="增值服务费">
                    ¥{currentBill.addon_fee.toFixed(2)}
                  </Descriptions.Item>
                  <Descriptions.Item label="折扣">
                    {currentBill.discount_amount > 0
                      ? `-¥${currentBill.discount_amount.toFixed(2)}`
                      : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="总金额">
                    <Text strong style={{ fontSize: 16, color: COLORS.primary }}>
                      ¥{currentBill.total_amount.toFixed(2)}
                    </Text>
                  </Descriptions.Item>
                </Descriptions>

                <Divider />

                {/* 支付信息 */}
                <Descriptions title="支付信息" column={1} bordered size="small">
                  <Descriptions.Item label="支付状态">
                    <Tag color={statusColorMap[currentBill.status]}>
                      {statusTextMap[currentBill.status]}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="支付时间">
                    {currentBill.paid_at
                      ? dayjs(currentBill.paid_at).format('YYYY-MM-DD HH:mm:ss')
                      : '-'}
                  </Descriptions.Item>
                </Descriptions>

                {/* 操作按钮 */}
                {currentBill.status === 'pending' && (
                  <>
                    <Divider />
                    <Button
                      type="primary"
                      block
                      onClick={() => {
                        window.location.href = `/settings/payment?invoice_id=${currentBill.id}&amount=${currentBill.total_amount}&subject=账单${currentBill.invoice_no}`;
                      }}
                    >
                      去支付
                    </Button>
                  </>
                )}

                {currentBill.invoice_url && (
                  <>
                    <Divider />
                    <Button
                      block
                      icon={<DownloadOutlined />}
                      onClick={() => handleDownload(currentBill.invoice_url!)}
                    >
                      下载发票
                    </Button>
                  </>
                )}
              </>
            )}
          </Spin>
        </Drawer>
      </div>
    </Spin>
  );
};
